import {
  Fragment,
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react';
import {
  analyzeByteBlocks,
  compileBytePattern,
  detectMagicBytes,
  findByteMatches,
  formatHexdumpLine,
  getHexdumpRowCount,
  interpretByteAt,
  parseByteOffset,
  resolveInputBytes,
  type ByteInputMode,
  type ByteOverviewBlock,
  type ByteSearchMode,
  type HexdumpByte,
  type NumericWidth,
} from '../../../shared/analysis';
import { useWorkspace } from '../../hooks/useWorkspace';

export type ByteColorScheme =
  | 'nibble'
  | 'categories'
  | 'frequency'
  | 'entropy'
  | 'colorblind'
  | 'none';

interface JumpRequest {
  offset: number;
  token: number;
}

interface VirtualHexdumpProps {
  bytes: Uint8Array;
  bytesPerLine: number;
  uppercase: boolean;
  offsetBase: 10 | 16;
  colorScheme: ByteColorScheme;
  frequency: Uint32Array;
  overview: ByteOverviewBlock[];
  selectedOffset: number | null;
  matchOffsets: Set<number>;
  jumpRequest: JumpRequest | null;
  onSelect: (offset: number) => void;
}

const ROW_HEIGHT = 25;
const VIEWPORT_HEIGHT = 384;
const OVERSCAN_ROWS = 6;

function byteCategory(byte: number): string {
  if (byte === 0) return 'null';
  if (byte === 0xff) return 'ff';
  if (byte === 9 || byte === 10 || byte === 13 || byte === 32) return 'whitespace';
  if (byte >= 32 && byte <= 126) return 'printable';
  if (byte < 32 || byte === 127) return 'control';
  return 'high';
}

function getBytePresentation(
  byte: HexdumpByte,
  scheme: ByteColorScheme,
  frequency: Uint32Array,
  maximumFrequency: number,
  overview: ByteOverviewBlock[],
): { className?: string; style?: CSSProperties } {
  if (scheme === 'nibble') return { className: `byte-color-${byte.colorGroup}` };
  if (scheme === 'categories') return { className: `byte-category-${byteCategory(byte.value)}` };
  if (scheme === 'colorblind') {
    if (byte.value === 0) return { className: 'byte-colorblind-null' };
    if (byte.value === 0xff) return { className: 'byte-colorblind-ff' };
    return { className: `byte-colorblind-${Math.floor(byte.value / 32)}` };
  }
  if (scheme === 'frequency') {
    const strength = maximumFrequency === 0 ? 0 : frequency[byte.value] / maximumFrequency;
    return {
      style: {
        color: `oklch(${68 + strength * 20}% ${0.08 + strength * 0.14} 210)`,
        backgroundColor: `rgb(52 214 255 / ${strength * 0.22})`,
      },
    };
  }
  if (scheme === 'entropy' && overview.length > 0) {
    const blockSize = overview[0].length;
    const block = overview[Math.min(overview.length - 1, Math.floor(byte.offset / blockSize))];
    const strength = block.entropy / 8;
    return {
      style: {
        color: `oklch(78% ${0.05 + strength * 0.15} ${110 + strength * 190})`,
        backgroundColor: `rgb(122 125 255 / ${strength * 0.18})`,
      },
    };
  }
  return {};
}

export function VirtualHexdump({
  bytes,
  bytesPerLine,
  uppercase,
  offsetBase,
  colorScheme,
  frequency,
  overview,
  selectedOffset,
  matchOffsets,
  jumpRequest,
  onSelect,
}: VirtualHexdumpProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const rowCount = getHexdumpRowCount(bytes.length, bytesPerLine);
  const maximumFrequency = Math.max(0, ...frequency);
  const firstRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS);
  const visibleRows = Math.ceil(VIEWPORT_HEIGHT / ROW_HEIGHT) + OVERSCAN_ROWS * 2;
  const lastRow = Math.min(rowCount, firstRow + visibleRows);

  useEffect(() => {
    if (!jumpRequest || !viewportRef.current) return;
    const row = Math.floor(jumpRequest.offset / bytesPerLine);
    viewportRef.current.scrollTop = Math.max(
      0,
      row * ROW_HEIGHT - VIEWPORT_HEIGHT / 2 + ROW_HEIGHT / 2,
    );
  }, [bytesPerLine, jumpRequest]);

  if (bytes.length === 0) {
    return <div className="glass rounded-md p-3 font-mono text-xs text-slate-400">No content</div>;
  }

  return (
    <div
      ref={viewportRef}
      className="glass overflow-auto rounded-md font-mono text-xs text-cyan-100"
      style={{ height: VIEWPORT_HEIGHT }}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      role="grid"
      aria-label="Virtualized hexdump"
      aria-rowcount={rowCount}
    >
      <div
        className="relative"
        style={{
          height: rowCount * ROW_HEIGHT,
          minWidth: `${13 + bytesPerLine * 4}ch`,
        }}
      >
        {Array.from({ length: lastRow - firstRow }, (_, index) => {
          const rowIndex = firstRow + index;
          const line = formatHexdumpLine(bytes, rowIndex, {
            bytesPerLine,
            uppercase,
            offsetBase,
          });
          return (
            <div
              key={line.startOffset}
              className="absolute left-0 flex h-[25px] items-center whitespace-pre px-3"
              style={{ top: rowIndex * ROW_HEIGHT }}
              role="row"
              aria-rowindex={rowIndex + 1}
            >
              <span className="inline-block w-[10ch] text-slate-500">{line.offset}</span>
              <span className="inline-block" style={{ width: `${bytesPerLine * 3}ch` }}>
                {line.bytes.map((byte, byteIndex) => {
                  const presentation = getBytePresentation(
                    byte,
                    colorScheme,
                    frequency,
                    maximumFrequency,
                    overview,
                  );
                  const selected = byte.offset === selectedOffset;
                  const match = matchOffsets.has(byte.offset);
                  return (
                    <Fragment key={byte.offset}>
                      {byteIndex > 0 && ' '}
                      <button
                        type="button"
                        className={`hex-byte ${presentation.className ?? ''} ${selected ? 'hex-byte-selected' : ''} ${match ? 'hex-byte-match' : ''}`}
                        style={presentation.style}
                        data-byte={byte.hex.toLowerCase()}
                        data-offset={byte.offset}
                        title={`Offset 0x${byte.offset.toString(16)}: 0x${byte.hex} (${byte.value})`}
                        aria-label={`Byte at offset ${byte.offset}: ${byte.hex}`}
                        onClick={() => onSelect(byte.offset)}
                      >
                        {byte.hex}
                      </button>
                    </Fragment>
                  );
                })}
              </span>
              <span className="ml-[2ch] inline-block">
                {line.bytes.map((byte) => {
                  const presentation = getBytePresentation(
                    byte,
                    colorScheme,
                    frequency,
                    maximumFrequency,
                    overview,
                  );
                  const selected = byte.offset === selectedOffset;
                  const match = matchOffsets.has(byte.offset);
                  return (
                    <button
                      type="button"
                      key={byte.offset}
                      className={`hex-ascii ${presentation.className ?? ''} ${selected ? 'hex-byte-selected' : ''} ${match ? 'hex-byte-match' : ''}`}
                      style={presentation.style}
                      tabIndex={-1}
                      aria-hidden="true"
                      onClick={() => onSelect(byte.offset)}
                    >
                      {byte.ascii}
                    </button>
                  );
                })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SourceControls() {
  const { input, inputMode, file, setInput, setInputMode, setFile } = useWorkspace();

  const chooseFile = async (nextFile: File | undefined) => {
    if (nextFile) await setFile(nextFile);
  };

  return (
    <section
      className="glass space-y-3 rounded-md p-3"
      onDrop={(event) => {
        event.preventDefault();
        void chooseFile(event.dataTransfer.files[0]);
      }}
      onDragOver={(event) => event.preventDefault()}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label>
          Input mode
          <select
            className="ml-2 rounded border border-white/10 bg-surface-800 px-2 py-1"
            value={inputMode}
            onChange={(event) => setInputMode(event.target.value as ByteInputMode)}
          >
            <option value="auto">Auto</option>
            <option value="hex">Hex</option>
            <option value="text">Text</option>
            <option value="base64">Base64</option>
            <option value="file">File</option>
          </select>
        </label>
        <label className="focus-ring cursor-pointer rounded border border-white/10 bg-surface-800 px-3 py-1.5 text-slate-200">
          Choose binary file
          <input
            className="sr-only"
            type="file"
            onChange={(event) => void chooseFile(event.currentTarget.files?.[0])}
          />
        </label>
        <span className="text-slate-500">or drop a file here</span>
      </div>

      {inputMode === 'file' ? (
        <div className="rounded border border-cyan-400/25 bg-cyan-500/5 p-3 text-sm text-slate-300">
          {file ? (
            <>
              <strong className="text-cyan-200">{file.name}</strong>
              <span className="ml-2 text-xs text-slate-400">
                {file.size.toLocaleString()} bytes{file.type ? ` · ${file.type}` : ''}
              </span>
            </>
          ) : (
            'Choose or drop a binary file.'
          )}
        </div>
      ) : (
        <textarea
          className="focus-ring h-28 w-full resize-none rounded-md border border-white/10 bg-surface-900/60 p-3 font-mono text-sm"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder={
            inputMode === 'base64'
              ? 'Paste Base64'
              : inputMode === 'hex'
                ? 'Paste hex bytes'
                : 'Paste hex, Base64, or text'
          }
        />
      )}
    </section>
  );
}

function OverviewStrip({
  blocks,
  onJump,
}: {
  blocks: ByteOverviewBlock[];
  onJump: (offset: number) => void;
}) {
  if (blocks.length === 0) return null;
  return (
    <section className="glass rounded-md p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs uppercase tracking-[0.12em] text-slate-400">Structure overview</h2>
        <div className="flex gap-2 text-[10px] text-slate-400">
          <span>Empty</span>
          <span>Text</span>
          <span>Repeated</span>
          <span>Structured</span>
          <span>High entropy</span>
        </div>
      </div>
      <div
        className="flex h-9 overflow-hidden rounded border border-white/10"
        aria-label="Byte structure overview"
      >
        {blocks.map((block) => (
          <button
            type="button"
            key={block.offset}
            className={`overview-${block.kind} min-w-px hover:brightness-125`}
            style={{ flexGrow: block.length }}
            title={`Offset 0x${block.offset.toString(16)} · entropy ${block.entropy.toFixed(2)} · ${block.kind}`}
            aria-label={`Jump to offset ${block.offset}, ${block.kind}, entropy ${block.entropy.toFixed(2)}`}
            onClick={() => onJump(block.offset)}
          />
        ))}
      </div>
    </section>
  );
}

function InterpretationPanel({ bytes, offset }: { bytes: Uint8Array; offset: number | null }) {
  const interpretation = useMemo(
    () => (offset === null || offset >= bytes.length ? null : interpretByteAt(bytes, offset)),
    [bytes, offset],
  );
  if (!interpretation) return null;

  const values: Array<[string, string | number | undefined]> = [
    ['Offset', `0x${interpretation.offset.toString(16)} (${interpretation.offset})`],
    ['Hex ahead', interpretation.hex],
    ['Binary', interpretation.binary],
    ['uint8 / int8', `${interpretation.unsigned8} / ${interpretation.signed8}`],
    ['ASCII / UTF-8', `${interpretation.ascii} / ${interpretation.utf8}`],
    ['uint16 LE / BE', pair(interpretation.uint16Le, interpretation.uint16Be)],
    ['int16 LE / BE', pair(interpretation.int16Le, interpretation.int16Be)],
    ['uint32 LE / BE', pair(interpretation.uint32Le, interpretation.uint32Be)],
    ['int32 LE / BE', pair(interpretation.int32Le, interpretation.int32Be)],
    ['float32 LE / BE', pair(interpretation.float32Le, interpretation.float32Be)],
    ['float64 LE / BE', pair(interpretation.float64Le, interpretation.float64Be)],
  ];

  return (
    <section className="glass rounded-md p-3">
      <h2 className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Selected byte</h2>
      <div className="grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-3">
        {values.map(([label, value]) => (
          <div key={label} className="rounded border border-white/10 bg-surface-800/50 px-2 py-1.5">
            <div className="text-slate-400">{label}</div>
            <div className="mt-0.5 break-all font-mono text-slate-200">
              {value ?? 'Not enough bytes'}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function pair(left: number | undefined, right: number | undefined): string | undefined {
  if (left === undefined || right === undefined) return undefined;
  const format = (value: number) => (Number.isFinite(value) ? String(value) : String(value));
  return `${format(left)} / ${format(right)}`;
}

export function HexdumpPage() {
  const { input, inputMode, file } = useWorkspace();
  const [bytesPerLine, setBytesPerLine] = useState(16);
  const [uppercase, setUppercase] = useState(false);
  const [offsetBase, setOffsetBase] = useState<10 | 16>(16);
  const [colorScheme, setColorScheme] = useState<ByteColorScheme>('nibble');
  const [selectedOffset, setSelectedOffset] = useState<number | null>(null);
  const [jumpRequest, setJumpRequest] = useState<JumpRequest | null>(null);
  const [goToInput, setGoToInput] = useState('');
  const [goToError, setGoToError] = useState('');
  const [searchMode, setSearchMode] = useState<ByteSearchMode>('hex');
  const [searchInput, setSearchInput] = useState('');
  const [numericWidth, setNumericWidth] = useState<NumericWidth>(1);
  const [byteOrder, setByteOrder] = useState<'little' | 'big'>('little');
  const [matches, setMatches] = useState<number[]>([]);
  const [matchLength, setMatchLength] = useState(0);
  const [matchIndex, setMatchIndex] = useState(0);
  const [searchError, setSearchError] = useState('');

  const resolved = useMemo(() => {
    try {
      return { ...resolveInputBytes(input, inputMode, file?.bytes ?? null), error: '' };
    } catch (error) {
      return {
        bytes: new Uint8Array(),
        mode: 'text' as const,
        error: error instanceof Error ? error.message : 'Unable to read input',
      };
    }
  }, [file, input, inputMode]);
  const bytes = resolved.bytes;
  const overview = useMemo(() => analyzeByteBlocks(bytes), [bytes]);
  const frequency = useMemo(() => {
    const counts = new Uint32Array(256);
    for (const byte of bytes) counts[byte] += 1;
    return counts;
  }, [bytes]);
  const magic = useMemo(() => detectMagicBytes(bytes), [bytes]);
  const matchOffsets = useMemo(() => {
    const offsets = new Set<number>();
    for (const match of matches) {
      for (let index = 0; index < matchLength; index += 1) offsets.add(match + index);
    }
    return offsets;
  }, [matchLength, matches]);

  useEffect(() => {
    setSelectedOffset((current) => (current !== null && current < bytes.length ? current : null));
    setMatches([]);
    setMatchLength(0);
    setSearchError('');
  }, [bytes]);

  const jumpTo = (offset: number) => {
    setSelectedOffset(offset);
    setJumpRequest({ offset, token: Date.now() + Math.random() });
  };

  const runSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearchError('');
    try {
      const pattern = compileBytePattern(searchInput, searchMode, {
        width: numericWidth,
        byteOrder,
      });
      startTransition(() => {
        const nextMatches = findByteMatches(bytes, pattern);
        setMatches(nextMatches);
        setMatchLength(pattern.values.length);
        setMatchIndex(0);
        if (nextMatches[0] !== undefined) jumpTo(nextMatches[0]);
      });
    } catch (error) {
      setMatches([]);
      setMatchLength(0);
      setSearchError(error instanceof Error ? error.message : 'Invalid search');
    }
  };

  const moveMatch = (direction: -1 | 1) => {
    if (matches.length === 0) return;
    const nextIndex = (matchIndex + direction + matches.length) % matches.length;
    setMatchIndex(nextIndex);
    jumpTo(matches[nextIndex]);
  };

  return (
    <section className="animate-rise space-y-3">
      <header>
        <h1 className="text-lg font-semibold text-slate-100">Hex Viewer and Hexdump</h1>
        <p className="text-sm text-slate-400">
          Inspect binary files with virtualized rows, synchronized byte selection, search, and
          structural color maps.
        </p>
      </header>

      <SourceControls />
      {resolved.error && (
        <div className="rounded border border-red-400/40 bg-red-500/10 p-2 text-sm text-red-300">
          {resolved.error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 text-xs">
        <label className="glass rounded px-2 py-1">
          Bytes/line
          <select
            className="ml-2 rounded border border-white/10 bg-surface-800 px-2 py-1"
            value={bytesPerLine}
            onChange={(event) => setBytesPerLine(Number(event.target.value))}
          >
            {[8, 16, 24, 32].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="glass rounded px-2 py-1">
          Offset base
          <select
            className="ml-2 rounded border border-white/10 bg-surface-800 px-2 py-1"
            value={offsetBase}
            onChange={(event) => setOffsetBase(Number(event.target.value) as 10 | 16)}
          >
            <option value={16}>Hex</option>
            <option value={10}>Decimal</option>
          </select>
        </label>
        <label className="glass rounded px-2 py-1">
          Byte colors
          <select
            className="ml-2 rounded border border-white/10 bg-surface-800 px-2 py-1"
            value={colorScheme}
            onChange={(event) => setColorScheme(event.target.value as ByteColorScheme)}
          >
            <option value="nibble">Nibble rainbow</option>
            <option value="categories">Byte categories</option>
            <option value="frequency">Frequency heatmap</option>
            <option value="entropy">Entropy heatmap</option>
            <option value="colorblind">Colorblind-safe</option>
            <option value="none">Monochrome</option>
          </select>
        </label>
        <label className="glass flex items-center gap-2 rounded px-2 py-1">
          <input
            type="checkbox"
            checked={uppercase}
            onChange={(event) => setUppercase(event.target.checked)}
          />
          Uppercase
        </label>
        <span className="glass rounded px-2 py-1 text-slate-400">
          {bytes.length.toLocaleString()} bytes · interpreted as {resolved.mode}
          {magic ? ` · ${magic.name}` : ''}
        </span>
      </div>

      <div className="grid gap-3 xl:grid-cols-[1fr_auto]">
        <form
          className="glass flex flex-wrap items-center gap-2 rounded-md p-2 text-xs"
          onSubmit={runSearch}
        >
          <select
            className="rounded border border-white/10 bg-surface-800 px-2 py-1.5"
            value={searchMode}
            onChange={(event) => setSearchMode(event.target.value as ByteSearchMode)}
            aria-label="Search mode"
          >
            <option value="hex">Hex / wildcards</option>
            <option value="ascii">ASCII / UTF-8</option>
            <option value="number">Number</option>
          </select>
          <input
            className="focus-ring min-w-48 flex-1 rounded border border-white/10 bg-surface-900/60 px-2 py-1.5 font-mono"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={
              searchMode === 'hex'
                ? 'DE AD ?? EF or A?'
                : searchMode === 'number'
                  ? '255 or 0xff'
                  : 'Text to find'
            }
          />
          {searchMode === 'number' && (
            <>
              <select
                className="rounded border border-white/10 bg-surface-800 px-2 py-1.5"
                value={numericWidth}
                onChange={(event) => setNumericWidth(Number(event.target.value) as NumericWidth)}
                aria-label="Numeric width"
              >
                <option value={1}>8-bit</option>
                <option value={2}>16-bit</option>
                <option value={4}>32-bit</option>
              </select>
              <select
                className="rounded border border-white/10 bg-surface-800 px-2 py-1.5"
                value={byteOrder}
                onChange={(event) => setByteOrder(event.target.value as 'little' | 'big')}
                aria-label="Byte order"
              >
                <option value="little">Little-endian</option>
                <option value="big">Big-endian</option>
              </select>
            </>
          )}
          <button
            type="submit"
            className="focus-ring rounded border border-white/10 bg-surface-800 px-3 py-1.5"
          >
            Find
          </button>
          <button
            type="button"
            className="focus-ring rounded border border-white/10 bg-surface-800 px-2 py-1.5"
            onClick={() => moveMatch(-1)}
            disabled={matches.length === 0}
          >
            Previous
          </button>
          <button
            type="button"
            className="focus-ring rounded border border-white/10 bg-surface-800 px-2 py-1.5"
            onClick={() => moveMatch(1)}
            disabled={matches.length === 0}
          >
            Next
          </button>
          <span className={searchError ? 'text-red-300' : 'text-slate-400'}>
            {searchError ||
              (matches.length > 0 ? `${matchIndex + 1} of ${matches.length}` : 'No active search')}
          </span>
        </form>

        <form
          className="glass flex items-center gap-2 rounded-md p-2 text-xs"
          onSubmit={(event) => {
            event.preventDefault();
            try {
              const offset = parseByteOffset(goToInput, bytes.length);
              setGoToError('');
              jumpTo(offset);
            } catch (error) {
              setGoToError(error instanceof Error ? error.message : 'Invalid offset');
            }
          }}
        >
          <input
            className="focus-ring w-28 rounded border border-white/10 bg-surface-900/60 px-2 py-1.5 font-mono"
            value={goToInput}
            onChange={(event) => setGoToInput(event.target.value)}
            placeholder="0x100"
            aria-label="Go to offset"
          />
          <button
            type="submit"
            className="focus-ring rounded border border-white/10 bg-surface-800 px-3 py-1.5"
          >
            Go
          </button>
          {goToError && <span className="max-w-48 text-red-300">{goToError}</span>}
        </form>
      </div>

      <OverviewStrip blocks={overview} onJump={jumpTo} />
      <VirtualHexdump
        bytes={bytes}
        bytesPerLine={bytesPerLine}
        uppercase={uppercase}
        offsetBase={offsetBase}
        colorScheme={colorScheme}
        frequency={frequency}
        overview={overview}
        selectedOffset={selectedOffset}
        matchOffsets={matchOffsets}
        jumpRequest={jumpRequest}
        onSelect={setSelectedOffset}
      />
      <InterpretationPanel bytes={bytes} offset={selectedOffset} />
    </section>
  );
}
